import { ROUTES } from '../../utils/constants';
import {
  TAMIL_COMPOSITION_ACTIVITY_ID,
  type TamilCompositionHomeStatus,
} from './api/tamilCompositionWriting.client';

export const TAMIL_COMPOSITION_LESSON_NO = 5;

export function buildTamilCompositionWritingStudioUrl(options?: { finalReview?: boolean }): string {
  if (options?.finalReview) {
    return `${ROUTES.STUDENT_TAMIL_COMPOSITION_WRITING}?final=1`;
  }
  return ROUTES.STUDENT_TAMIL_COMPOSITION_WRITING;
}

export function buildTamilCompositionTutorLiveUrl(options?: {
  resumeReview?: boolean;
  lessonNo?: number;
}): string {
  const params = new URLSearchParams();
  params.set('lesson_no', String(options?.lessonNo ?? TAMIL_COMPOSITION_LESSON_NO));
  params.set('activity_id', TAMIL_COMPOSITION_ACTIVITY_ID);
  if (options?.resumeReview) {
    params.set('composition_review', '1');
  }
  return `${ROUTES.STUDENT_TAMIL_TUTOR}?${params.toString()}`;
}

export function isTamilCompositionWaitingStep(step: string | undefined | null): boolean {
  return String(step || '').trim().toLowerCase().startsWith('waiting_for_');
}

export function isTamilCompositionStudioWritingStep(step: string | undefined | null): boolean {
  const normalized = String(step || '').trim().toLowerCase();
  return isTamilCompositionWaitingStep(normalized) || normalized.endsWith('_needs_revision');
}

export function compositionHomeStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'NOT_STARTED':
      return 'Not started';
    case 'BRAINSTORMING':
      return 'Brainstorming';
    case 'WRITING':
      return 'Writing in progress';
    case 'READY_FOR_FINAL_REVIEW':
      return 'Final review';
    case 'NEEDS_REVISION':
      return 'Needs revision';
    case 'SUBMITTED':
      return 'Completed';
    default:
      return status || 'Not started';
  }
}

export function compositionHomePrimaryCta(status: TamilCompositionHomeStatus | null): string | null {
  if (!status) return 'Start';
  switch (status.next_action) {
    case 'start':
      return 'Start';
    case 'continue_writing':
      return 'Continue Writing';
    case 'continue_tutor':
    case 'continue_tutor_review':
      return 'Continue';
    case 'view_feedback':
      return 'View Feedback';
    case 'continue_final_review':
      return 'Final Review';
    default:
      return 'Continue';
  }
}

export function compositionHomeCtaRoute(status: TamilCompositionHomeStatus | null): string {
  if (!status) return buildTamilCompositionTutorLiveUrl();
  if (status.next_action === 'continue_writing') {
    return buildTamilCompositionWritingStudioUrl();
  }
  if (status.next_action === 'continue_final_review') {
    return buildTamilCompositionWritingStudioUrl({ finalReview: true });
  }
  return buildTamilCompositionTutorLiveUrl({
    resumeReview: status.next_action === 'continue_tutor_review',
  });
}

export function compactSectionProgressLines(
  rows: TamilCompositionHomeStatus['section_progress'],
): string[] {
  const reviewed = rows.filter((r) => r.status === 'REVIEWED');
  const current = rows.find((r) =>
    ['DRAFTING', 'READY_FOR_REVIEW', 'UNDER_REVIEW', 'NEEDS_REVISION'].includes(r.status),
  );
  const lines: string[] = [];
  for (const row of reviewed.slice(-3)) {
    lines.push(`✓ ${row.label_ta}`);
  }
  if (current) {
    lines.push(`→ ${current.label_ta}`);
  } else {
    const next = rows.find((r) => r.status === 'NOT_STARTED');
    if (next) lines.push(`Next: ${next.label_ta}`);
  }
  return lines;
}
